import filterInvoicesDeliveryEntities from './filterInvoicesDeliveryEntities.js'
import generateDynamicFields from './generateDynamicFields.js'

const buildDeliveryEntitiesCollection = (mockDatabase, filters = {}) => {
  // Générer les champs dynamiques avec les métriques correctes
  const { enrichedInvoices, entityMetrics, globalMetrics } = generateDynamicFields(mockDatabase)
  const { commerce_items: products, mdm_entities, crm_companies } = mockDatabase

  // Créer des mappings pour les noms
  const entityIdToName = {}
  const entityIdToCompany = {}

  // Mapper les delivery entities (branches) vers leurs noms
  mdm_entities.forEach(entity => {
    if (entity.is_deliverable && entity.is_billable) {
      // Trouver le nom de la branche dans les adresses CRM
      const company = crm_companies.find(c => c.id === entity.crm_company_id)
      if (company) {
        const address = company.addresses.find(a => a.id === entity.id)
        entityIdToName[entity.id] = address ? address.address1 : entity.id
        entityIdToCompany[entity.id] = company.name
      }
    }
  })

  // --- FILTRAGE ---
  const filteredInvoices = filterInvoicesDeliveryEntities(enrichedInvoices, filters)

  // Si pas de filtres actifs, utiliser les métriques globales directement
  let filteredGlobalMetrics = globalMetrics

  // Si filtres actifs, recalculer uniquement les métriques filtrées
  if (filters.entity !== 'ALL' || filters.currency !== 'ALL' || filters.period === 'YTD') {
    const filteredEntityIds = new Set(filteredInvoices.map(inv => inv.delivery_entity_id))
    const filteredEntityMetrics = Object.fromEntries(
      Object.entries(entityMetrics).filter(([entityId]) => filteredEntityIds.has(entityId)),
    )

    const allFilteredEntities = Object.values(filteredEntityMetrics)
    const onboardedBranches = allFilteredEntities.filter(e => e.isOnboarded).length
    const recurringBranches = allFilteredEntities.filter(e => e.hasRecurringOrders && !e.isChurned).length
    const churnedBranches = allFilteredEntities.filter(e => e.isChurned).length

    // Calcul du churn rate et retention selon la nouvelle logique
    const churnRate = onboardedBranches > 0 ? (churnedBranches / onboardedBranches) * 100 : 0
    const retentionRatio = 100 - churnRate

    const totalRecurringOrders = allFilteredEntities
      .filter(e => !e.isChurned)
      .reduce((sum, e) => sum + e.recurringOrders, 0)

    const activeRecurringBranches = allFilteredEntities.filter(e => e.hasRecurringOrders && !e.isChurned).length
    const avgRecurringOrdersPerBranch = activeRecurringBranches > 0 ? totalRecurringOrders / activeRecurringBranches : 0

    const recurringBranchesWithSales = allFilteredEntities.filter(e => e.hasRecurringOrders && !e.isChurned && e.avgRecurringSales > 0)
    const totalAvgRecurringSales = recurringBranchesWithSales.reduce((sum, e) => sum + e.avgRecurringSales, 0)
    const avgSalesRecurringOrders = recurringBranchesWithSales.length > 0 ? totalAvgRecurringSales / recurringBranchesWithSales.length : 0

    filteredGlobalMetrics = {
      ...globalMetrics, // Garde les métriques techniques (normalCycleDays, etc.)
      onboardedBranches,
      recurringBranches,
      churnedBranches,
      retentionRatio: Math.round(retentionRatio * 100) / 100,
      churnRate: Math.round(churnRate * 100) / 100,
      totalRecurringOrders,
      avgRecurringOrdersPerBranch: Math.round(avgRecurringOrdersPerBranch * 100) / 100,
      avgSalesRecurringOrders: Math.round(avgSalesRecurringOrders * 100) / 100,
    }
  }

  // Grouper par delivery entity
  const deliveryEntitiesMap = {}
  const branchesMap = {}

  filteredInvoices.forEach(invoice => {
    const deliveryEntityId = invoice.delivery_entity_id
    const deliveryEntityName = entityIdToName[deliveryEntityId] || deliveryEntityId
    const buyerEntityName = entityIdToCompany[deliveryEntityId] || 'Unknown'

    if (!deliveryEntitiesMap[deliveryEntityId]) {
      deliveryEntitiesMap[deliveryEntityId] = {
        name: deliveryEntityName,
        totalSales: 0,
        totalPairs: 0,
        products: {},
        branches: new Set(),
        lastOrderDate: null,
        // Nouveaux champs dynamiques
        isOnboarded: invoice.isOnboarded,
        hasRecurringOrders: invoice.entity_has_recurring_orders,
        isChurned: invoice.entity_is_churned,
        retentionStatus: invoice.entity_retention_status,
        totalOrders: invoice.entity_total_orders,
        recurringOrders: invoice.entity_recurring_orders,
        avgRecurringSales: invoice.entity_avg_recurring_sales,
        daysSinceLastOrder: invoice.days_since_entity_last_order,
        daysSinceFirstOrder: invoice.days_since_entity_first_order,
      }
    }

    // Grouper les branches globalement
    if (!branchesMap[deliveryEntityName]) {
      branchesMap[deliveryEntityName] = {
        name: deliveryEntityName,
        pairs: 0,
        sales: 0,
        lastOrderDate: null,
      }
    }

    const entity = deliveryEntitiesMap[deliveryEntityId]
    const branch = branchesMap[deliveryEntityName]

    // Calculer totaux à partir des lineitems
    let invoicePairs = 0
    let invoiceAmount = 0

    invoice.lineitems.forEach(lineitem => {
      invoicePairs += lineitem.unit_quantity || 0
      invoiceAmount += lineitem.total_amount || 0

      // Grouper les produits
      const productId = lineitem.item_id
      if (!entity.products[productId]) {
        const product = products.find(p => p.id === productId)
        entity.products[productId] = {
          code: productId,
          name: product?.name || productId,
          pairs: 0,
          sales: 0,
        }
      }
      entity.products[productId].pairs += lineitem.unit_quantity || 0
      entity.products[productId].sales += lineitem.total_amount || 0
    })

    entity.totalSales += invoiceAmount
    entity.totalPairs += invoicePairs
    entity.branches.add(buyerEntityName)

    branch.pairs += invoicePairs
    branch.sales += invoiceAmount

    // Dernière date de commande
    const invoiceDate = new Date(invoice.created_at)
    if (!entity.lastOrderDate || invoiceDate > entity.lastOrderDate) {
      entity.lastOrderDate = invoiceDate
    }
    if (!branch.lastOrderDate || invoiceDate > branch.lastOrderDate) {
      branch.lastOrderDate = invoiceDate
    }
  })

  // Calculer les Top 20 Products globalement
  const globalProductsMap = {}

  filteredInvoices.forEach(invoice => {
    invoice.lineitems.forEach(lineitem => {
      const productId = lineitem.item_id
      if (!globalProductsMap[productId]) {
        const product = products.find(p => p.id === productId)
        globalProductsMap[productId] = {
          code: productId,
          name: product?.name || productId,
          pairs: 0,
          sales: 0,
        }
      }
      globalProductsMap[productId].pairs += lineitem.unit_quantity || 0
      globalProductsMap[productId].sales += lineitem.total_amount || 0
    })
  })

  const globalTopProducts = Object.values(globalProductsMap)
    .sort((a, b) => b.sales - a.sales)
    .slice(0, 20)

  // Convertir en tableau et trier
  const deliveryEntitiesArray = Object.values(deliveryEntitiesMap).map(entity => ({
    ...entity,
    branches: entity.branches.size,
    products: Object.values(entity.products).sort((a, b) => b.sales - a.sales).slice(0, 20),
    lastOrderFormatted: entity.lastOrderDate
      ? entity.lastOrderDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'N/A',
    noReorder: entity.lastOrderDate
      ? (new Date() - entity.lastOrderDate) / (1000 * 60 * 60 * 24) > 365 ? 1 : 0 : 1,
  }))

  // Convertir les branches en tableau
  const branchesArray = Object.values(branchesMap)
    .sort((a, b) => b.sales - a.sales)
    .slice(0, 20)
    .map(branch => ({
      ...branch,
      lastOrderFormatted: branch.lastOrderDate
        ? branch.lastOrderDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : 'N/A',
    }))

  return {
    deliveryEntities: deliveryEntitiesArray.sort((a, b) => b.totalSales - a.totalSales),
    topProducts: globalTopProducts,
    topBranches: branchesArray,
    // Métriques globales filtrées
    globalMetrics: filteredGlobalMetrics,
  }
}

export default buildDeliveryEntitiesCollection